import { CardSelected } from "../../type";
import { FaEdit, FaTrash } from "react-icons/fa";
import {
  ActionWrapper,
  CardImage,
  CardName,
  CardWrapper,
  DescriptionSpan,
  DescriptionWrapper,
  InformationWrapper,
  SubTitle,
} from "./Cards.styled";
import { AiFillStar } from "react-icons/ai";
import { FiAlertTriangle, FiEdit2 } from "react-icons/fi";
import { CardMarketCompatible } from "../common/CardMarketCompatible/CardMarketCompatible";
import ErrorIconWithTooltip from "../common/ErrorIconWithTooltip/ErrorIconWithTooltip";
import { Box, FlexBox, Interactive } from "../primitives";
import { Price } from "./Price";
import { Quantity } from "./Quantity";
import { Condition } from "./Condition";
import { Langugage } from "./Language";
import { useCards } from "../../context/CardsContext";
import { FirstEditionIcon } from "../common/CardsIconExtra/FirstEditionIcon";
import { ReversIcon } from "../common/CardsIconExtra/ReversIcon";
import { InfoTooltip } from "../common/InfoTooltip/InfoTooltip";

interface CardsProps {
  card: CardSelected;
  hasAction?: boolean;
  onDeleteCard?: (arg1: string | null) => void;
  setEditCard?: (arg1: string | null) => void;
  setOpenModalChoosePrice?: (arg1: string | null) => void;
  cardsClicked?: string[];
  onCardSelectHandler?: (arg1: string) => void;
}

export const Cards = ({
  card,
  hasAction = false,
  onDeleteCard = undefined,
  setEditCard,
  setOpenModalChoosePrice,
  onCardSelectHandler,
  cardsClicked,
}: CardsProps) => {
  const cardComplexArray = useCards();
  const foundCardComplexArray = cardComplexArray[card.id];
  const isFoil = card.finish === "foil";
  const cardMarketId =
    foundCardComplexArray?.card_market_ids?.length === 2 && isFoil
      ? foundCardComplexArray?.card_market_ids?.[1]
      : foundCardComplexArray?.card_market_ids?.[0];

  return (
    <CardWrapper
      key={card.id}
      onClick={() => {
        onCardSelectHandler?.(card.uid);
      }}
      css={{
        boxShadow: cardsClicked?.includes(card.uid)
          ? "0 0 8px 3px #166534"
          : "0 4px 4px rgba(0,0,0,0.4)",
        border: cardsClicked?.includes(card.uid)
          ? "1px solid #166534"
          : "1px solid #ddd",
      }}
    >
      <CardMarketCompatible cardmarketProductId={cardMarketId} />
      <Box>
        <CardName>
          <a
            href={`https://www.cardtrader.com/cards/${card.id}`}
            target="_blank"
            rel="noreferrer"
          >
            {foundCardComplexArray?.name}
          </a>
        </CardName>
        <SubTitle>
          {`#${foundCardComplexArray?.fixed_properties?.collector_number} ${foundCardComplexArray?.editionCode} - ${foundCardComplexArray?.editionName}`}
        </SubTitle>
        <FlexBox>
          <CardImage
            src={foundCardComplexArray?.image_url}
            alt={foundCardComplexArray?.name}
          />
          <InformationWrapper>
            <Price
              card={card}
              setOpenModalChoosePrice={setOpenModalChoosePrice}
            />
            <Quantity card={card} />
            <Condition
              condition={card.condition}
              originalCondition={card.originalCondition}
            />
            <Langugage
              language={card.language}
              originalLanguage={card.originalLanguage}
            />
            <FlexBox css={{ gap: 5 }}>
              {card.finish === "foil" && (
                <InfoTooltip content="Foil">
                  <AiFillStar size={24} color="#FFD700" />
                </InfoTooltip>
              )}
              {card.signed && (
                <InfoTooltip content="Signed">
                  <FiEdit2 size={24} color={"black"} />
                </InfoTooltip>
              )}
            </FlexBox>
            <FlexBox css={{ gap: 5, alignItems: "center" }}>
              {card.isFirstEdition && (
                <InfoTooltip content="First Edition">
                  <Box>
                    <FirstEditionIcon />
                  </Box>
                </InfoTooltip>
              )}
              {card.isReversed && (
                <InfoTooltip content="Reverse">
                  <Box>
                    <ReversIcon />
                  </Box>
                </InfoTooltip>
              )}
            </FlexBox>
            <FlexBox css={{ alignItem: "center", gap: 5 }}>
              {card?.uploadingError && (
                <Box>
                  <ErrorIconWithTooltip message={card?.uploadingError} />
                </Box>
              )}
              {card?.warningDetailCard && (
                <InfoTooltip content={card.warningDetailCard}>
                  <Box>
                    <FiAlertTriangle color="orange" size={22} />
                  </Box>
                </InfoTooltip>
              )}
            </FlexBox>
            {hasAction && (
              <ActionWrapper>
                <Interactive
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditCard?.(card.uid);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.keyCode === 13) {
                      e.preventDefault(); // stop default "click"
                      e.stopPropagation(); // stop bubbling
                    }
                  }}
                >
                  <FaEdit size={26} color="blue" />
                </Interactive>
                {typeof onDeleteCard === "function" && (
                  <Interactive
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteCard?.(card.uid);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.keyCode === 13) {
                        e.preventDefault(); // stop default "click"
                        e.stopPropagation(); // stop bubbling
                      }
                    }}
                  >
                    <FaTrash size={22} color="red" />
                  </Interactive>
                )}
              </ActionWrapper>
            )}
          </InformationWrapper>
        </FlexBox>
        <DescriptionWrapper>
          <DescriptionSpan>{card.description}</DescriptionSpan>
        </DescriptionWrapper>
      </Box>
    </CardWrapper>
  );
};
